import { type Writable, writable, get } from "svelte/store";
import * as _ from "lodash-es";
import reactHookFormValidateField from "./Form/validateField/validateField";

export function createFormStore(initialValues = {}, submitForm = async () => {}) {
  type FormStore = {
    values: { [key: string]: any };
    errors: { [key: string]: any };
    refs: { [key: string]: any };
    validationRules: { [key: string]: any };
    submitForm: () => void;
  };

  const store: Writable<FormStore> = writable({
    values: initialValues,
    errors: {},
    validationRules: {},
    refs: {},
    submitForm,
  });

  const validateField = async (name: string) => {
    // Clone the current state
    const currentState = get(store);
    const newState = { ...currentState };
    if (!newState.refs[name]) {
      // don't try to validate fields that don't correspond to a UI element
      return;
    } else if (!newState.refs[name]?.isConnected && !newState.refs[name]?.[0]?.isConnected) {
      // ref was registered, but then unmounted.
      // Having this check here prevents having to unregister refs when dynamic fields
      // are removed
      return;
    }

    // Get the field
    // this field object is a construct of the react-hook-form library
    const field = {
      ref: _.isArray(newState.refs[name]) ? newState.refs[name][0] : newState.refs[name],
      refs: _.isArray(newState.refs[name]) ? newState.refs[name] : null,
      name: name,
      ...newState.validationRules[name],
    };

    // Validate the field
    const error = await reactHookFormValidateField(field, newState.values, true);

    // Update the state
    if (Object.keys(error).length > 0) {
      newState.errors[field.name] = error[field.name];
      store.set(newState);
    } else {
      delete newState.errors[field.name];
      await store.set(newState);
    }

    // Return the error
    return error;
  };

  const validate = async () => {
    // Reset the errors
    const currentState = get(store);
    const newState = { ...currentState, errors: {} };
    await store.set(newState);

    for (const name of Object.keys(newState.values)) {
      await validateField(name);
    }

    return await get(store).errors;
  };

  return {
    subscribe: store.subscribe,
    addRef: (field: string, ref: HTMLElement | HTMLElement[]) => {
      store.update((state) => {
        state.refs[field] = ref;
        return state;
      });
    },
    setField: (field: string, value: any) => {
      store.update((state) => {
        state.values[field] = value;
        return state;
      });
    },
    addValidationRules: (field: string, validationRules: { [key: string]: any }) => {
      store.update((state) => {
        state.validationRules[field] = validationRules;
        return state;
      });
    },
    validate: validate,
    register: (field: string) => {
      store.update((state) => {
        // state.values could already contain a value for this field in the case of an edit form
        state.values[field] = state.values[field] || "";
        return state;
      });
      const onChange = async (e: Event) => {
        await store.update((state) => {
          state.values[field] = (e.target as HTMLSelectElement).value;
          return state;
        });
        // on change, validate the field if it has been touched previously
        if (get(store).errors[field]) {
          await validateField(field);
        }
      };
      return { onChange };
    },
  };
}
